import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleCard from '@/components/feature/ArticleCard';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

export default function Home() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [subscribeStatus, setSubscribeStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [subscribeError, setSubscribeError] = useState('');

  const featuredArticles = articles.filter(a => a.featured).slice(0, 6);
  const latestArticles = articles.slice(0, 6);

  const faqItems = [
    {
      q: '「不喜欢色色的我吗」想表达的真实情感是什么？',
      a: '这句话表面轻松俏皮，实则表达了一种深层的情感渴望——希望被无条件地接纳，包括那些不那么完美的部分。它反映了当代年轻人在亲密关系中普遍存在的自我怀疑和对真实被看见的期待。',
    },
    {
      q: '如何在亲密关系中勇敢表达真实的情感需求？',
      a: '首先，用"我感到……"而非"你总是……"开头；其次，将需求具体化而非笼统表达；第三，选择合适的时机而非在冲突中表达；最后，接纳对方可能需要时间消化。记住，表达需求不是添麻烦，而是给对方靠近你的机会。',
    },
    {
      q: '自我接纳和安于现状有什么区别？',
      a: '自我接纳是看清自己的全部——包括优点和不足——然后对这一切说"是的，这就是我"。它不是放弃成长，而是带着对自己的善意去成长。安于现状是不愿意改变，而自我接纳是改变的最佳起点。',
    },
    {
      q: '情侣之间如何有效倾听对方的情感需求？',
      a: '放下手机、放下评判、放下急于解决的心态。用"反射式倾听"复述对方的话来确认理解，用开放性问题邀请对方分享更多，给沉默留出空间。最重要的是——让对方感到"我在听，我在乎，我理解"。',
    },
    {
      q: '如何知道自己属于哪种依恋类型？',
      a: '观察自己在亲密关系中的反应模式：是否在对方没有及时回复时极度焦虑？是否在关系变深时本能地想拉开距离？是否对伴侣的情绪变化过度敏感？这些信号可以帮助你初步判断。如需更准确的评估，建议咨询专业心理咨询师。',
    },
  ];

  const handleSubscribe = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formEl = e.currentTarget;
    const honeypot = (formEl.querySelector('input[name="website_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setSubscribeStatus('success');
      setTimeout(() => setSubscribeStatus('idle'), 3000);
      return;
    }
    setSubscribeStatus('loading');
    setSubscribeError('');
    try {
      const formData = new FormData(formEl);
      const res = await fetch('https://readdy.ai/api/form/d9q58mue3oq4jqumtgm0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(responseText); } catch { /* not json */ }
      if (res.ok && parsed?.code === 'OK') {
        setSubscribeStatus('success');
        setEmail('');
        setTimeout(() => setSubscribeStatus('idle'), 5000);
      } else {
        const msg = (parsed?.meta as Record<string, string>)?.message || (parsed?.message as string) || responseText || '';
        if (msg.toLowerCase().includes('spam') || msg.includes('form data is spam')) {
          setSubscribeError('系统检测到异常提交，请稍后再试。');
        } else {
          setSubscribeError(msg || t('section.newsletter.error'));
        }
        setSubscribeStatus('error');
      }
    } catch {
      setSubscribeError(t('section.newsletter.error'));
      setSubscribeStatus('error');
    }
  };

  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: t('site.name'),
    url: 'https://xinyugongming.com',
    description: t('site.description'),
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://xinyugongming.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: t('site.name'),
    url: 'https://xinyugongming.com',
    description: t('site.description'),
    sameAs: ['https://weibo.com/xinyugongming'],
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map(item => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.a,
      },
    })),
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(websiteSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        {/* Hero Section */}
        <header className="relative w-full h-[600px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=A%20warm%20abstract%20minimalist%20illustration%20of%20soft%20overlapping%20circles%20in%20peach%20cream%20and%20warm%20beige%20tones%2C%20representing%20emotional%20connection%20and%20healing%2C%20gentle%20gradients%2C%20dreamy%20atmosphere%2C%20no%20text%2C%20no%20people%2C%20modern%20editorial%20background&width=1600&height=900&seq=hero-main-2024&orientation=landscape"
            alt="心语共鸣"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50"></div>
          <div className="relative z-10 w-full max-w-4xl mx-auto px-4 md:px-6 text-center">
            <h1 className="font-heading text-3xl md:text-5xl font-bold text-background-50 mb-4 leading-tight">
              {t('hero.title')}
            </h1>
            <p className="text-lg md:text-xl text-background-50/90 mb-3 font-medium">
              {t('hero.subtitle')}
            </p>
            <p className="text-sm md:text-base text-background-50/70 max-w-2xl mx-auto mb-8 leading-relaxed">
              {t('hero.description')}
            </p>
            <div className="flex items-center justify-center gap-4 flex-wrap">
              <Link
                to="/category/self-identity"
                className="bg-primary-500 text-background-50 px-6 py-3 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                {t('hero.cta')}
              </Link>
              <Link
                to="/about"
                className="border border-background-50/50 text-background-50 px-6 py-3 rounded-full text-sm font-medium hover:bg-background-50/10 transition-colors cursor-pointer whitespace-nowrap"
              >
                {t('hero.secondary')}
              </Link>
            </div>
          </div>
        </header>

        <main>
          {/* Featured Articles */}
          <section className="py-14 md:py-20">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="text-center mb-12">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                  {t('section.featured')}
                </h2>
                <p className="text-foreground-500 text-sm md:text-base">
                  {t('section.featured.desc')}
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {featuredArticles.map(article => (
                  <ArticleCard key={article.id} article={article} />
                ))}
              </div>
            </div>
          </section>

          {/* Categories */}
          <section className="py-14 md:py-20 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="text-center mb-12">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                  {t('section.categories')}
                </h2>
                <p className="text-foreground-500 text-sm md:text-base">
                  {t('section.categories.desc')}
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {categories.map(cat => (
                  <Link
                    key={cat.id}
                    to={`/category/${cat.slug}`}
                    className="group flex items-start gap-4 p-5 bg-background-50 rounded-lg border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                  >
                    <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600 group-hover:bg-accent-100 group-hover:text-accent-600 transition-colors shrink-0">
                      <i className={`${cat.icon} text-xl`}></i>
                    </div>
                    <div>
                      <h3 className="font-heading text-base font-semibold text-foreground-900 mb-1 group-hover:text-primary-500 transition-colors">
                        {cat.name}
                      </h3>
                      <p className="text-sm text-foreground-500 leading-relaxed mb-2">{cat.description}</p>
                      <span className="text-xs text-foreground-400">{cat.articleCount} 篇文章</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* Latest Articles */}
          <section className="py-14 md:py-20">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="text-center mb-12">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                  最新文章
                </h2>
                <p className="text-foreground-500 text-sm md:text-base">
                  发现更多关于情感与亲密关系的深度思考
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {latestArticles.map(article => (
                  <ArticleCard key={article.id} article={article} />
                ))}
              </div>
              <div className="text-center mt-10">
                <Link
                  to="/category/self-identity"
                  className="inline-flex items-center gap-2 bg-foreground-900 text-background-50 px-6 py-3 rounded-full text-sm font-medium hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
                >
                  浏览全部文章
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-line text-sm"></i>
                  </div>
                </Link>
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section className="py-14 md:py-20 bg-background-100">
            <div className="max-w-3xl mx-auto px-4 md:px-6">
              <div className="text-center mb-12">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                  {t('section.faq')}
                </h2>
                <p className="text-foreground-500 text-sm md:text-base">
                  {t('section.faq.desc')}
                </p>
              </div>
              <div className="space-y-4">
                {faqItems.map((item, index) => (
                  <details key={index} className="group bg-background-50 rounded-lg border border-background-200/70 overflow-hidden">
                    <summary className="flex items-center justify-between p-5 cursor-pointer list-none">
                      <span className="font-heading text-base font-medium text-foreground-900 pr-4">{item.q}</span>
                      <div className="w-6 h-6 flex items-center justify-center shrink-0 text-foreground-400 group-open:hidden">
                        <i className="ri-add-line"></i>
                      </div>
                      <div className="w-6 h-6 flex items-center justify-center shrink-0 text-foreground-400 hidden group-open:flex">
                        <i className="ri-subtract-line"></i>
                      </div>
                    </summary>
                    <div className="px-5 pb-5">
                      <p className="text-sm text-foreground-600 leading-relaxed">{item.a}</p>
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </section>

          {/* Newsletter CTA */}
          <section className="py-14 md:py-20 bg-gradient-to-r from-primary-500 to-accent-500">
            <div className="max-w-2xl mx-auto px-4 md:px-6 text-center">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-background-50 mb-3">
                {t('section.newsletter')}
              </h2>
              <p className="text-background-50/80 text-sm md:text-base mb-8">
                {t('section.newsletter.desc')}
              </p>
              <form
                onSubmit={handleSubscribe}
                data-readdy-form=""
                className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
              >
                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="honeypot-field"
                />
                <input
                  type="email"
                  name="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('section.newsletter.placeholder')}
                  className="flex-1 px-5 py-3 rounded-full text-sm bg-background-50 text-foreground-900 placeholder-foreground-300 focus:outline-none focus:ring-2 focus:ring-background-50/50"
                />
                <button
                  type="submit"
                  disabled={subscribeStatus === 'loading'}
                  className="bg-foreground-900 text-background-50 px-6 py-3 rounded-full text-sm font-semibold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
                >
                  {subscribeStatus === 'loading' ? '提交中...' : t('section.newsletter.button')}
                </button>
              </form>
              {subscribeStatus === 'success' && (
                <p className="mt-4 text-sm text-background-50 font-medium">{t('section.newsletter.success')}</p>
              )}
              {subscribeStatus === 'error' && subscribeError && (
                <p className="mt-4 text-sm text-background-50/90">{subscribeError}</p>
              )}
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}